
import React from 'react';
import { Card } from "@/components/ui/card";
import { Brain } from 'lucide-react';
import type { SubjectMasteryData } from './types';
import { categories } from '@/types/learning-path';

interface SubjectPerformanceProps {
  subjectAnalysis: Record<string, SubjectMasteryData>;
  onSubjectSelect?: (subject: string) => void;
}

const SubjectPerformance: React.FC<SubjectPerformanceProps> = ({ 
  subjectAnalysis, 
  onSubjectSelect 
}) => {
  // Filter to only include subjects with attempt data
  const attemptedSubjects = React.useMemo(() => {
    return Object.keys(subjectAnalysis).filter(subject => 
      subjectAnalysis[subject].attempts > 0
    ).sort();
  }, [subjectAnalysis]);

  const getMasteryLevel = (data?: SubjectMasteryData) => {
    if (!data || data.attempts === 0) return "Not Started";
    const avgScore = data.totalScore / data.attempts;
    if (avgScore >= 90) return "Expert";
    if (avgScore >= 80) return "Advanced";
    if (avgScore >= 70) return "Intermediate";
    if (avgScore >= 60) return "Basic";
    return "Beginner";
  };

  const getMasteryColor = (level: string) => {
    switch (level) {
      case "Expert":
        return "bg-green-100 text-green-800";
      case "Advanced":
        return "bg-blue-100 text-blue-800";
      case "Intermediate":
        return "bg-yellow-100 text-yellow-800";
      case "Not Started":
        return "bg-gray-100 text-gray-600";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Show message when no attempted subjects
  if (attemptedSubjects.length === 0) {
    return (
      <section>
        <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
          <Brain className="w-6 h-6 text-purple-500" />
          Subject Performance Overview
        </h2>
        <Card className="p-8 text-center">
          <p className="text-gray-600 mb-2">No subject performance data available yet.</p>
          <p className="text-sm text-gray-500">Complete quizzes to see your subject performance.</p>
        </Card>
      </section>
    );
  }

  return (
    <section>
      <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
        <Brain className="w-6 h-6 text-purple-500" />
        Subject Performance Overview
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {attemptedSubjects.map((subject) => {
          const data = subjectAnalysis[subject];
          const masteryLevel = getMasteryLevel(data);
          
          return (
            <Card 
              key={subject} 
              className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              onClick={() => onSubjectSelect?.(subject)}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-lg">{subject}</h3>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getMasteryColor(masteryLevel)}`}>
                  {masteryLevel}
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Average Score</span>
                  <span className="font-medium">
                    {data ? (data.totalScore / data.attempts).toFixed(1) + '%' : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Best Score</span>
                  <span className="font-medium">
                    {data ? data.highScore + '%' : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Attempts</span>
                  <span className="font-medium">{data?.attempts || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Recent Trend</span>
                  {data?.recentScores?.length > 0 ? (
                    <span className={`font-medium ${
                      data.recentScores[data.recentScores.length - 1] > data.recentScores[0]
                        ? "text-green-600"
                        : "text-red-600"
                    }`}>
                      {data.recentScores[data.recentScores.length - 1] > data.recentScores[0]
                        ? "↑ Improving"
                        : "↓ Needs Focus"}
                    </span>
                  ) : (
                    <span className="text-gray-500">No data</span>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </section>
  );
};

export default SubjectPerformance;
